import { CategorySummary } from "../types/store";

export interface FlatCategoryNode {
  category: CategorySummary;
  depth: number;
}

const matchesCategory = (category: CategorySummary, value: string) =>
  category.name === value || category.slug === value;

export const flattenCategoryTree = (
  categories: CategorySummary[],
  depth = 0,
): FlatCategoryNode[] =>
  categories.flatMap((category) => [
    { category, depth },
    ...flattenCategoryTree(category.subcategories || [], depth + 1),
  ]);

export const findCategoryNode = (
  categories: CategorySummary[],
  value: string,
): CategorySummary | null => {
  for (const category of categories) {
    if (matchesCategory(category, value)) {
      return category;
    }
    const nestedMatch = findCategoryNode(category.subcategories || [], value);
    if (nestedMatch) {
      return nestedMatch;
    }
  }
  return null;
};

export const findCategoryPath = (
  categories: CategorySummary[],
  value: string,
  path: CategorySummary[] = [],
): CategorySummary[] => {
  for (const category of categories) {
    const nextPath = [...path, category];
    if (matchesCategory(category, value)) {
      return nextPath;
    }
    const nestedPath = findCategoryPath(category.subcategories || [], value, nextPath);
    if (nestedPath.length) {
      return nestedPath;
    }
  }
  return [];
};

export const findCategoryPathById = (
  categories: CategorySummary[],
  id: number,
  path: CategorySummary[] = [],
): CategorySummary[] => {
  for (const category of categories) {
    const nextPath = [...path, category];
    if (category.id === id) {
      return nextPath;
    }
    const nestedPath = findCategoryPathById(category.subcategories || [], id, nextPath);
    if (nestedPath.length) {
      return nestedPath;
    }
  }
  return [];
};

export const getCategoryDescendantIds = (category: CategorySummary): number[] => [
  ...(category.id ? [category.id] : []),
  ...(category.subcategories || []).flatMap(getCategoryDescendantIds),
];
